import type {Result, ValidationError} from 'framework';
import type {CopyDay} from '../application/use-cases/copy-day';
import type {CopyMeal} from '../application/use-cases/copy-meal';
import type {CreateManualMeal} from '../application/use-cases/create-manual-meal';
import type {DeleteMeal} from '../application/use-cases/delete-meal';
import type {EditMeal} from '../application/use-cases/edit-meal';
import type {GetMealDetail} from '../application/use-cases/get-meal-detail';
import type {GetDailyDashboard} from '../application/use-cases/get-daily-dashboard';
import type {CopyDayCommand} from '../domain/copy-day-command';
import type {CopyMealCommand} from '../domain/copy-meal-command';
import type {CreateManualMealCommand} from '../domain/create-manual-meal-command';
import type {DailyDashboard} from '../domain/daily-dashboard';
import type {EditMealCommand} from '../domain/edit-meal-command';
import type {LocalDate} from '../domain/local-date.value-object';
import type {MealDetail} from '../domain/meal-detail';
import type {MealId} from '../domain/meal-id.value-object';
import type {DashboardError} from '../domain/errors';

export interface DashboardControllerGetDailyInput {
  readonly date: LocalDate | null;
}

export interface DashboardControllerDeleteMealInput {
  readonly mealId: MealId;
}

export interface DashboardControllerGetMealDetailInput {
  readonly mealId: MealId;
}

export interface DashboardControllerEditMealInput {
  readonly mealId: MealId;
  readonly command: EditMealCommand;
}

export interface DashboardControllerCreateManualMealInput {
  readonly command: CreateManualMealCommand;
}

export interface DashboardControllerCopyMealInput {
  readonly mealId: MealId;
  readonly command?: CopyMealCommand;
}

export interface DashboardControllerCopyDayInput {
  readonly command: CopyDayCommand;
}

/**
 * Thin facade that the loader and UI call through. Keeps React Query
 * factories and route loaders free of use-case imports.
 */
export class DashboardController {
  constructor(
    private readonly getDailyDashboardUseCase: GetDailyDashboard,
    private readonly getMealDetailUseCase: GetMealDetail,
    private readonly deleteMealUseCase: DeleteMeal,
    private readonly editMealUseCase: EditMeal,
    private readonly createManualMealUseCase: CreateManualMeal,
    private readonly copyMealUseCase: CopyMeal,
    private readonly copyDayUseCase: CopyDay,
  ) {}

  getDaily(
    input: DashboardControllerGetDailyInput,
  ): Promise<Result<DailyDashboard, DashboardError>> {
    return this.getDailyDashboardUseCase.execute({date: input.date});
  }

  deleteMeal(input: DashboardControllerDeleteMealInput): Promise<Result<void, DashboardError>> {
    return this.deleteMealUseCase.execute({mealId: input.mealId});
  }

  getMealDetail(
    input: DashboardControllerGetMealDetailInput,
  ): Promise<Result<MealDetail, DashboardError>> {
    return this.getMealDetailUseCase.execute({mealId: input.mealId});
  }

  editMeal(
    input: DashboardControllerEditMealInput,
  ): Promise<Result<MealDetail, DashboardError | ValidationError>> {
    return this.editMealUseCase.execute({mealId: input.mealId, command: input.command});
  }

  createManualMeal(
    input: DashboardControllerCreateManualMealInput,
  ): Promise<Result<MealDetail, DashboardError | ValidationError>> {
    return this.createManualMealUseCase.execute({command: input.command});
  }

  copyMeal(input: DashboardControllerCopyMealInput): Promise<Result<MealDetail, DashboardError>> {
    return this.copyMealUseCase.execute({mealId: input.mealId, command: input.command});
  }

  copyDay(
    input: DashboardControllerCopyDayInput,
  ): Promise<Result<readonly MealDetail[], DashboardError>> {
    return this.copyDayUseCase.execute({command: input.command});
  }
}
